/**
 * 사용자 프로필 관련 유틸리티 함수들
 */

import type { ITokenManager } from '@edusense/shared-types-auth';
import type { IIdeMessagingService } from '@edusense/shared-types-ide';
import type { AuthState } from '@edusense/shared-types-auth';
import type { User } from '@edusense/shared-types-user';
import { 
  USER_KEY,
  VsCodeAuthResponse,
  OidcUserInfo 
} from './types';
import type { ResolvedStorageKeys } from '../storage-keys';
import { resolveStorageKeys } from '../storage-keys';

interface ProfileConfig {
  tokenManager: ITokenManager;
  apiService: any;
  isVSCodeEnv: boolean;
  ideMessengerInstance?: IIdeMessagingService | null;
  setAuthState: (state: AuthState | ((prev: AuthState) => AuthState)) => void;
  lastTokenRef: React.MutableRefObject<string | null>;
  storageKeys?: Partial<ResolvedStorageKeys>;
}

/**
 * 사용자 프로필 가져오기 함수 생성
 */
export const createFetchUserProfile = (config: ProfileConfig) => async (token: string) => {
  try {
    const storageKeys = resolveStorageKeys(config.storageKeys);
    // 중복 요청 방지
    if (config.lastTokenRef.current === token) {
      console.log('[AuthUtils] 같은 토큰으로 이미 처리됨, 스킵:', token.substring(0, 10) + '...');
      return;
    }

    config.lastTokenRef.current = token;
    console.log('[AuthUtils] 사용자 프로필 가져오기 시도');

    // 토큰 설정
    config.tokenManager.setToken(token);
    config.apiService.setToken(token);

    // 웹 환경에서는 이미 저장된 사용자 정보를 사용
    if (!config.isVSCodeEnv) {
      const storedUser = localStorage.getItem(storageKeys.userKey);
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser) as User;
          config.setAuthState({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            error: null
          });

          console.log('[AuthUtils] 사용자 프로필 설정 완료:', user.username);
          return;
        } catch (parseError) {
          console.warn('[AuthUtils] 저장된 사용자 정보 파싱 실패:', parseError);
        }
      }
      return; // 웹 환경에서는 API 호출하지 않음
    }

    // VSCode 환경에서는 계정 라벨 기반으로 프로필 가져오기
    if (config.isVSCodeEnv && config.ideMessengerInstance) {
      try {
        const sessionInfo = await config.ideMessengerInstance.request<VsCodeAuthResponse>('getControlPlaneSessionInfo', {});
        if (sessionInfo?.account?.label) {
          const profileResponse = await config.apiService.getAxiosInstance().get(`/api/v1/users/username/${sessionInfo.account.label}`);
          if (profileResponse?.data) {
            const user: User = {
              id: profileResponse.data.id || profileResponse.data.uuid || sessionInfo.account.id,
              username: profileResponse.data.username || sessionInfo.account.label,
              name: profileResponse.data.name || sessionInfo.account.label,
              email: profileResponse.data.email,
              profileImage: profileResponse.data.profileImage,
              avatar: profileResponse.data.avatar || profileResponse.data.profileImage
            };

            config.setAuthState({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
              error: null
            });

            console.log('[AuthUtils] Username 기반 사용자 프로필 설정 완료:', user.username);
            return;
          }
        }
      } catch (usernameError) {
        console.error('[AuthUtils] Username 기반 프로필 가져오기 실패:', usernameError);
      }

      // VSCode 계정 정보 기반으로 기본 사용자 생성
      try {
        const sessionInfo = await config.ideMessengerInstance.request<VsCodeAuthResponse>('getControlPlaneSessionInfo', {});
        if (sessionInfo?.account) {
          const defaultUser: User = {
            id: sessionInfo.account.id,
            username: sessionInfo.account.label || 'user',
            name: sessionInfo.account.label || 'VSCode User',
            email: undefined,
            profileImage: undefined,
            avatar: undefined
          };

          config.setAuthState({
            user: defaultUser,
            token,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            error: null
          });

          console.log('[AuthUtils] VSCode 기본 사용자 정보 생성 완료:', defaultUser.username);
          return;
        }
      } catch (sessionError) {
        console.error('[AuthUtils] VSCode 세션 정보 가져오기 실패:', sessionError);
      }
    }

    // 최종적으로 실패한 경우에만 오류 상태 설정
    config.setAuthState(prev => ({ ...prev, isLoading: false, isInitialized: true, error: '사용자 정보를 가져올 수 없습니다' }));

  } catch (error) {
    console.error('[AuthUtils] fetchUserProfile 중 오류:', error);
    config.setAuthState(prev => ({ ...prev, isLoading: false, isInitialized: true, error: '인증 중 오류가 발생했습니다' }));
  }
};

/**
 * 사용자 이메일 가져오기 함수 생성  
 */
export const createFetchUserEmail = (config: ProfileConfig) => async (authState: AuthState, updateUser: (userData: Partial<User>) => void): Promise<string | undefined> => {
  console.log('[AuthUtils] 사용자 이메일 가져오기 시도');
  const storageKeys = resolveStorageKeys(config.storageKeys);

  if (!authState.token || !authState.user) {
    console.warn('[AuthUtils] 토큰 또는 사용자 정보가 없어 이메일을 가져올 수 없습니다.');
    return undefined;
  }

  // 이미 이메일이 있다면 반환
  if (authState.user.email) {
    console.log('[AuthUtils] 이미 사용자 이메일 정보가 존재:', authState.user.email);
    return authState.user.email;
  }

  try {
    const USERINFO_ENDPOINT = `${import.meta.env.VITE_AUTH_SERVER_URL || 'https://auth.ugot.uk'}/oauth2/userinfo`;
    const tokenType = 'Bearer';
    const response = await config.apiService.getAxiosInstance().get<OidcUserInfo>(
      USERINFO_ENDPOINT,
      { headers: { 'Authorization': `${tokenType} ${authState.token}` } }
    );

    console.log('[AuthUtils] Userinfo 응답 전체 데이터:', JSON.stringify(response.data, null, 2));

    // OIDC userinfo 표준 필드 매핑 (서버 구현 참조: OidcUserInfoConfig.createUserInfoClaims)
    // sub, preferred_username, email, email_verified, name, picture
    let { email, name, picture, preferred_username } = response?.data || {};

    // 보강: userinfo.picture가 없으면 저장된 ID 토큰에서 picture 추출 시도
    if (!(typeof picture === 'string' && picture.trim() !== '')) {
      try {
        const idToken = localStorage.getItem(storageKeys.idTokenKey);
        if (idToken) {
          const [_, payloadB64] = idToken.split('.') as [string, string];
          if (payloadB64) {
            const json = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
            if (typeof json?.picture === 'string' && json.picture.trim() !== '') {
              picture = json.picture;
            }
            // 누락된 항목 보강
            if (!email && typeof json?.email === 'string') email = json.email;
            if (!name && typeof json?.name === 'string') name = json.name;
            if (!preferred_username && typeof json?.preferred_username === 'string') preferred_username = json.preferred_username;
          }
        }
      } catch (e) {
        console.warn('[AuthUtils] ID 토큰 기반 picture 보강 실패:', (e as any)?.message);
      }
    }

    if (email) {
      console.log('[AuthUtils] 이메일 정보 획득:', email);
      updateUser({ 
        email,
        // 표준 picture를 프론트의 profileImage로 동기화
        profileImage: typeof picture === 'string' && picture.trim() !== '' ? picture : authState.user?.profileImage,
        // name 업데이트(표시명)
        name: typeof name === 'string' && name.trim() !== '' ? name : authState.user?.name,
        // username은 preferred_username가 있으면 보정 (기존 값 우선)
        username: authState.user?.username || preferred_username
      });
      // picture가 여전히 비어있다면, 프로필 API로 avatarUrl 보강 시도
      if (!(typeof picture === 'string' && picture.trim() !== '')) {
        try {
          const username = authState.user?.username || (response?.data?.preferred_username as string | undefined);
          if (username && (config.apiService as any)?.getAxiosInstance) {
            const axios = (config.apiService as any).getAxiosInstance();
            const url = `/api/v1/users/username/${encodeURIComponent(username)}/profile`;
            console.log('[AuthUtils] 보강용 프로필 API 호출:', url);
            const profResp = await axios.get(url);
            const body = profResp?.data || {};
            const candidate = body?.profile?.avatarUrl || body?.avatarUrl || body?.profileImageUrl || body?.profileImage;
            if (typeof candidate === 'string' && candidate.trim() !== '') {
              updateUser({ profileImage: candidate });
            }
          }
        } catch (e) {
          console.warn('[AuthUtils] 보강용 프로필 API 호출 실패:', (e as any)?.message);
        }
      }
      return email;
    } else {
      console.warn('[AuthUtils] Userinfo 응답에서 이메일을 찾을 수 없습니다. 응답 데이터:', response?.data);
      
      // 다른 필드에서 이메일 찾기 시도
      const alternativeEmail = response?.data?.['email_verified'] || 
                               response?.data?.['user_email'] || 
                               response?.data?.['mail'] ||
                               response?.data?.['emailAddress'];
      
      if (alternativeEmail) {
        console.log('[AuthUtils] 대체 필드에서 이메일 발견:', alternativeEmail);
        updateUser({ email: alternativeEmail });
        return alternativeEmail;
      }
      
      // 이메일이 없더라도 picture/name이 온 경우 사용자 정보 동기화
      if (picture || name || preferred_username) {
        updateUser({
          profileImage: typeof picture === 'string' && picture.trim() !== '' ? picture : authState.user?.profileImage,
          name: typeof name === 'string' && name.trim() !== '' ? name : authState.user?.name,
          username: authState.user?.username || preferred_username
        });
      }
      return undefined;
    }
  } catch (error) {
    console.error('[AuthUtils] 이메일 가져오기 실패:', error);
    return undefined;
  }
};
